/**
 * EthereumApiClient
 * 
 * Handles all Ethereum-related API operations
 */

import { BaseApiClient } from './BaseApiClient';

export interface EthereumWallet {
  address: string;
  network: string;
  chainId: number;
  balance?: {
    balanceEth: number;
    balanceUsd: number;
    ethPrice: number;
  };
}

export interface EthereumTransaction {
  _id?: string;
  txHash: string;
  from: string;
  to: string;
  amount: number;
  amountUsd: number;
  type: 'deposit' | 'withdraw';
  status: 'pending' | 'completed' | 'failed';
  confirmations: number;
  fee?: number;
  gasUsed?: number;
  createdAt: string;
  completedAt?: string;
  explorerUrl?: string;
}

export interface DepositInfo {
  transactionId: string;
  depositAddress: string;
  amount: number;
  estimatedUsd: number;
  qrCode: string;
  minimumDeposit: number;
  confirmationsRequired: number;
  instructions: string;
  explorerUrl?: string;
}

export interface WithdrawalResult {
  success: boolean;
  transactionId: string;
  txHash: string;
  amount: number;
  amountUsd: number;
  fee: number;
  explorerUrl: string;
}

export interface GasEstimate {
  gasLimit: number;
  gasPrice: string;
  gasCostEth: number;
  gasCostUsd: number;
  ethPrice: number;
}

export interface ServiceStatus {
  enabled: boolean;
  network: string;
  chainId: number;
  blockNumber: number;
  ethPrice: number;
  gasPrice: {
    low: string;
    medium: string;
    high: string;
  };
  watchedAddresses: number;
  pendingTransactions: number;
}

export class EthereumApiClient extends BaseApiClient {
  // ============================================
  // Wallet Management
  // ============================================

  /**
   * Create a new Ethereum wallet
   */
  async createWallet(): Promise<EthereumWallet> {
    return this.post<EthereumWallet>('/ethereum/wallet/create');
  }

  /**
   * Import an existing wallet
   */
  async importWallet(data: { privateKey?: string; mnemonic?: string }): Promise<EthereumWallet> {
    return this.post<EthereumWallet>('/ethereum/wallet/import', data);
  }

  /**
   * Get wallet information
   */
  async getWallet(): Promise<{ hasWallet: boolean; wallet?: EthereumWallet }> {
    return this.get<{ hasWallet: boolean; wallet?: EthereumWallet }>('/ethereum/wallet');
  }

  /**
   * Get wallet balance
   */
  async getBalance(): Promise<{
    balanceEth: number;
    balanceUsd: number;
    ethPrice: number;
    lastUpdated: string;
  }> {
    return this.get<{
      balanceEth: number;
      balanceUsd: number;
      ethPrice: number;
      lastUpdated: string;
    }>('/ethereum/balance');
  }

  // ============================================
  // Deposit Operations
  // ============================================

  /**
   * Create a deposit address
   */
  async createDepositAddress(): Promise<{
    address: string;
    network: string;
    qrCode: string;
    minimumDeposit: number;
    confirmationsRequired: number;
  }> {
    return this.post<{
      address: string;
      network: string;
      qrCode: string;
      minimumDeposit: number;
      confirmationsRequired: number;
    }>('/ethereum/deposit/address');
  }

  /**
   * Initiate a deposit
   */
  async initiateDeposit(amount: number): Promise<DepositInfo> {
    return this.post<DepositInfo>('/ethereum/deposit', { amount });
  }

  // ============================================
  // Withdrawal Operations
  // ============================================

  /**
   * Withdraw ETH to an address
   */
  async withdraw(amount: number, toAddress: string): Promise<WithdrawalResult> {
    return this.post<WithdrawalResult>('/ethereum/withdraw', {
      amount,
      toAddress
    });
  }

  // ============================================
  // Transaction Operations
  // ============================================

  /**
   * Get transaction details by hash
   */
  async getTransaction(txHash: string): Promise<{
    hash: string;
    from: string;
    to: string;
    value: string;
    confirmations: number;
    status: 'pending' | 'completed' | 'failed';
    explorerUrl: string;
  }> {
    return this.get<{
      hash: string;
      from: string;
      to: string;
      value: string;
      confirmations: number;
      status: 'pending' | 'completed' | 'failed';
      explorerUrl: string;
    }>(`/ethereum/transaction/${txHash}`);
  }

  /**
   * Get transaction history
   */
  async getTransactionHistory(limit = 50, skip = 0): Promise<{
    transactions: EthereumTransaction[];
    total: number;
    hasMore: boolean;
  }> {
    return this.get<{
      transactions: EthereumTransaction[];
      total: number;
      hasMore: boolean;
    }>(`/ethereum/transactions?limit=${limit}&skip=${skip}`);
  }

  /**
   * Get transaction status by ID
   */
  async getTransactionStatus(transactionId: string): Promise<{
    status: 'pending' | 'processing' | 'completed' | 'failed';
    confirmations: number;
    txHash?: string;
    explorerUrl?: string;
  }> {
    return this.get<{
      status: 'pending' | 'processing' | 'completed' | 'failed';
      confirmations: number;
      txHash?: string;
      explorerUrl?: string;
    }>(`/ethereum/transaction/status/${transactionId}`);
  }

  // ============================================
  // Utility Operations
  // ============================================

  /**
   * Validate an Ethereum address
   */
  async validateAddress(address: string): Promise<{
    address: string;
    isValid: boolean;
    network?: string;
  }> {
    return this.post<{
      address: string;
      isValid: boolean;
      network?: string;
    }>('/ethereum/validate-address', { address });
  }

  /**
   * Get gas estimate for a transaction
   */
  async estimateGas(amount: number, toAddress: string): Promise<GasEstimate> {
    return this.post<GasEstimate>('/ethereum/gas-estimate', {
      amount,
      toAddress
    });
  }

  /**
   * Get service status
   */
  async getServiceStatus(): Promise<ServiceStatus> {
    return this.get<ServiceStatus>('/ethereum/status');
  }

  /**
   * Resolve ENS name to address
   */
  async resolveENSName(ensName: string): Promise<{
    address: string;
    isValid: boolean;
    ensName: string;
  }> {
    return this.post<{
      address: string;
      isValid: boolean;
      ensName: string;
    }>('/ethereum/resolve-ens', { ensName });
  }

  /**
   * Reverse resolve address to ENS name
   */
  async lookupENSName(address: string): Promise<{
    ensName: string | null;
    hasName: boolean;
  }> {
    return this.post<{
      ensName: string | null;
      hasName: boolean;
    }>('/ethereum/lookup-ens', { address });
  }
}
